import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { getPartnerAccountGroupMapping$ } from '@vendasta/protos/partner/v1/partner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ParticipantType } from '@vendasta/conversation';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import { SalespersonService } from '@vendasta/sales';
import {
  Observable,
  ReplaySubject,
  catchError,
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { MARKETING_SERVICES_PIDS, SUPPORT_PID } from '../../../../core/src/lib/conversation.constants';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { CONVERSATION_HOST_APP_INTERFACE_TOKEN, PARTNER_ID_TOKEN } from '../../../../core/src/lib/tokens';

interface SalesContact {
  name?: string | null;
  image?: string | null;
}

@Component({
  selector: 'inbox-conversation-assignee',
  templateUrl: './inbox-conversation-assignee.component.html',
  styleUrls: ['./inbox-conversation-assignee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, TranslateModule, GalaxyAvatarModule],
})
export class InboxConversationAssigneeComponent {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));

  private conversationDetails$$ = new ReplaySubject<ConversationDetail | null>(1);
  private conversationDetails$ = this.conversationDetails$$.asObservable().pipe(distinctUntilChanged());

  conversationInfo$ = this.conversationDetails$.pipe(
    map((details) => {
      if (!details) return null;
      const res = this.hostAppInterface.getRecipient(details);
      if (Array.isArray(res) && res.length > 0) {
        return res[0];
      } else if (!Array.isArray(res) && res) {
        return res;
      } else {
        throw new Error(this.translateService.instant('ERROR_LOADING_RECIPIENT'));
      }
    }),
    catchError((error) => {
      this.snackbarService.openErrorSnack(error);
      return of(null);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  accountGroupPartnerId$ = this.conversationInfo$.pipe(
    switchMap((accountGroupOrPartner) => {
      const partnerId = this.partnerId();
      if (
        accountGroupOrPartner?.participantType === ParticipantType.PARTICIPANT_TYPE_PARTNER &&
        partnerId &&
        (partnerId === SUPPORT_PID || MARKETING_SERVICES_PIDS.includes(partnerId))
      ) {
        return this.getMappedAccountPartner(accountGroupOrPartner.partnerId);
      } else if (accountGroupOrPartner?.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP) {
        return of(accountGroupOrPartner.accountGroupId);
      } else {
        return of(null);
      }
    }),
    catchError(() => of(null)),
  );

  assignedContact$ = this.accountGroupPartnerId$.pipe(
    filterNullAndUndefined(),
    switchMap((accountGroupId) => this.getSalesPersonDetails(accountGroupId)),
    switchMap((salesPerson) => this.getSalesPersonContactInfo(salesPerson.partnerId, salesPerson.salesPersonId)),
  );

  readonly isAccountGroupPartnerType$ = this.accountGroupPartnerId$.pipe(
    map((account) => !!account),
    defaultIfEmpty(false),
  );

  @Input() set conversationDetail(details: ConversationDetail | null) {
    this.conversationDetails$$.next(details);
  }

  constructor(
    private readonly accountGroupService: AccountGroupService,
    private readonly translateService: TranslateService,
    private readonly salespersonService: SalespersonService,
    private readonly snackbarService: SnackbarService,
  ) {}

  getSalesPersonDetails(accountGroupId: string): Observable<{ salesPersonId: string; partnerId: string }> {
    return this.accountGroupService
      .get(accountGroupId, new ProjectionFilter({ accountGroupExternalIdentifiers: true }))
      .pipe(
        filter((res) => !!res),
        map((res) => ({
          salesPersonId: res.externalIdentifiers?.salesPersonId || '',
          partnerId: res.externalIdentifiers?.partnerId || '',
        })),
        defaultIfEmpty({ salesPersonId: '', partnerId: '' }),
      );
  }

  getSalesPersonContactInfo(partnerId: string, salesPersonId: string): Observable<SalesContact> {
    if (partnerId !== '' && salesPersonId !== '') {
      return this.salespersonService.getMultiSalesperson(partnerId, [salesPersonId]).pipe(
        map((res) => res?.[0]),
        filter((res) => !!res),
        map((contact): SalesContact => {
          return {
            name:
              [contact.firstName, contact.lastName].join(' ').trim().length > 0
                ? [contact.firstName, contact.lastName].join(' ').trim()
                : this.translateService.instant('INBOX.TOPBAR.NONE'),
            image: contact.photoUrlHttps ? contact.photoUrlHttps : '',
          };
        }),
        defaultIfEmpty<SalesContact, SalesContact>({
          name: this.translateService.instant('INBOX.TOPBAR.NONE'),
          image: null,
        }),
      );
    } else {
      return of({
        name: this.translateService.instant('INBOX.TOPBAR.NONE'),
        image: null,
      });
    }
  }

  getMappedAccountPartner(partnerId: string): Observable<string> {
    return getPartnerAccountGroupMapping$({ partnerId, accountGroupPartnerId: '' })
      .pipe(
        map((accountGroupMapping) => {
          return accountGroupMapping.accountGroupId || '';
        }),
      );
  }
}
